import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { groupBy, sortBy } from 'lib/array/arrayUtils'
import useCurrentLoyaltyActivities from 'loyalty/hooks/useCurrentLoyaltyActivities'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LoyaltyAccountLandingPageUserActivity from './LoyaltyAccountLandingPageUserActivity'
import Caption from 'LuxKit/Typography/Caption'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineMinusIcon from 'LuxKit/Icons/line/LineMinusIcon'
import LineRedoIcon from 'LuxKit/Icons/line/LineRedoIcon'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import LayoutContainer from 'components/Common/LayoutContainer'
import Sheet from 'components/utils/Sheet'

const DisclaimerText = styled(BodyText)`
  max-width: ${rem(580)};
  margin-inline: auto;
`

const ActivitiesList = styled(Group)`
  ${mediaQueryUp.tablet} {
    padding-inline-end: ${rem(20)};
  }
`

const DESKTOP_ACTIVITY_TABLE_COLUMNS = `auto minmax(0, 1fr) ${rem(80)} ${rem(60)} ${rem(92)} ${rem(56)}`
const INFINITE_ACTIVITY_TABLE_COLUMNS = `auto minmax(0, 1fr) ${rem(80)} ${rem(60)}`

const TableHeader = styled(Sheet)`
  display: grid;
  gap: ${rem(24)};
  grid-template-columns: ${DESKTOP_ACTIVITY_TABLE_COLUMNS};

  &.is-infinite {
    grid-template-columns: ${INFINITE_ACTIVITY_TABLE_COLUMNS};
  }
`

const TableHeaderPoints = styled(BodyText)`
  padding-inline-start: ${rem(5)};
`

const TableHeaderMetric = styled(BodyText)`
  padding-inline-start: ${rem(8)};
`

function LoyaltyAccountLandingPageUserActivities() {
  const intl = useIntl()
  const [activities, fetchingActivities, hasNextPage, loadMore] = useCurrentLoyaltyActivities()
  const [pendingPoints, fetchingPendingPoints] = useCurrentLoyaltyPendingPoints()
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)

  const allActivities = useMemo(() => {
    const pointActivities = pendingPoints.items.map((pending): App.LoyaltyAccountActivity => {
      return {
        points: pending.points,
        basePoints: pending.basePoints,
        bonusPoints: pending.bonusPoints,
        date: pending.createdAt,
        id: pending.id,
        statusCredits: pending.statusCredits,
        type: 'pending_points',
        orderId: pending.orderId,
        orderItemId: pending.orderItemId,
        pendingNights: pending.numNights,
      }
    })
    return sortBy(activities.concat(pointActivities), activity => new Date(activity.date), 'desc')
  }, [activities, pendingPoints.items])

  const activitiesByYear = useMemo(() => {
    const groups = groupBy(allActivities, activity => new Date(activity.date).getFullYear())
    return groups.values().map(group => sortBy(group, activity => new Date(activity.date), 'desc')).toArray()
  }, [allActivities])

  const isInitialLoad = (fetchingPendingPoints || fetchingActivities) && activities.length === 0
  const isLoadingMore = fetchingActivities && activities.length > 0

  const yourActivityLabel = intl.formatMessage({
    id: 'Ry1xSjXw1gJHfqrbdVXk',
    defaultMessage: 'Your activity',
    description: 'Heading for loyalty account activity section in the loyalty section',
  })

  const emptyStateText = intl.formatMessage({
    id: '_w7UQefIC30jPL8n-gKq',
    defaultMessage: 'You’re on your way to great rewards. Your transactions for earning and using points, as well as redeeming benefits will appear here when you make bookings.',
    description: 'Empty state message when user has no loyalty activity yet',
  })

  const pointsEarnedLabel = intl.formatMessage({
    id: 'OV0vMoDFQv-QuFIZ39Pa',
    defaultMessage: 'Earned',
    description: 'Legend label for points earned in loyalty activity',
  })

  const pointsUsedLabel = intl.formatMessage({
    id: 'aR8E_5N9gIR6qMGJdqGk',
    defaultMessage: 'Used',
    description: 'Legend label for points used in loyalty activity on the promotional landing page',
  })

  const pointsPendingLabel = intl.formatMessage({
    id: '1_fZTFbzZUDzv5WaZSrA',
    defaultMessage: 'Pending',
    description: 'Legend label for points pending in loyalty activity',
  })

  const pointsHeader = intl.formatMessage({
    id: 'hzbnP4_LPAHL1ObHXTeD',
    defaultMessage: 'Points',
    description: 'Table column header for points in loyalty activity',
  })

  const statusHeader = intl.formatMessage({
    id: '9u0CUCEOS-13Hcx-7h7n',
    defaultMessage: 'Status Credits',
    description: 'Table column header for status credits in loyalty activity',
  })

  const nightsHeader = intl.formatMessage({
    id: '5Ep2sq69xGBPGLtbFV7_',
    defaultMessage: 'Nights',
    description: 'Table column header for nights in loyalty activity',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'NAZEn1xsUl4E7uTQaGO9',
    defaultMessage: 'Show more',
    description: 'Button text to load more loyalty activities on account page',
  })

  const disclaimerText = intl.formatMessage({
    id: 'dW0cyfwTzGGGZ66QkBDr',
    defaultMessage: 'Please note some transactions may take up to 24 hours to appear after you complete your trip. Any purchases made prior to joining Société will not earn points.',
    description: 'Disclaimer that activity feed updates may lag and that pre-enrolment purchases do not earn Société Points',
  })

  const pendingInfoLabel = intl.formatMessage({
    id: 'GmrwuS37myA9DNjC2Mvz',
    defaultMessage: 'Pending: Your account will be credited the day after you complete your trip.',
    description: 'Caption under Your activity when pending earn exists, clarifying travel credits post the day after trip completion',
  })

  return <Sheet id="loyalty-your-activity" padding="24 0" tabletPadding="48 0" background="normal-background">
    <LayoutContainer variant="medium">
      <VerticalSpacer gap={16}>
        <Heading variant="heading3">{yourActivityLabel}</Heading>
        {!isInitialLoad && allActivities.length > 0 && <CaptionTextBlock variant="medium" startIcon={<LineRedoIcon colour="warning" />}>
          {pendingInfoLabel}
        </CaptionTextBlock>}
        {isInitialLoad && <LoadingIndicator inline />}
        {!isInitialLoad && <>
          {activities.length === 0 && <BodyText variant="large">
            {emptyStateText}
          </BodyText>}
          {allActivities.length > 0 && <VerticalSpacer gap={12}>
            <CSSBreakpoint min="tablet">
              <Group direction="horizontal" gap={12} horizontalAlign="end">
                <CaptionTextBlock variant="medium" startIcon={<LinePlusIcon colour="success" />}>
                  {pointsEarnedLabel}
                </CaptionTextBlock>
                <CaptionTextBlock variant="medium" startIcon={<LineMinusIcon colour="critical" />}>
                  {pointsUsedLabel}
                </CaptionTextBlock>
                <CaptionTextBlock variant="medium" startIcon={<LineRedoIcon colour="warning" />}>
                  {pointsPendingLabel}
                </CaptionTextBlock>
              </Group>
            </CSSBreakpoint>
            <VerticalSpacer gap={20}>
              {activitiesByYear.map(activities => <VerticalSpacer gap={8} key={activities[0].date}>
                <CSSBreakpoint max="mobile">
                  <Sheet background="light-background" padding="6 12">
                    <Caption variant="large" weight="bold" colour="light-foreground-alt">
                      <FormatDateTime value={activities[0].date} year="numeric" />
                    </Caption>
                  </Sheet>
                </CSSBreakpoint>
                <CSSBreakpoint min="tablet">
                  <TableHeader background="light-background" padding="4 20 4 0" className={isInfiniteTier ? 'is-infinite' : undefined}>
                    <div />
                    <Caption variant="large" weight="bold" colour="light-foreground-alt">
                      <FormatDateTime value={activities[0].date} year="numeric" />
                    </Caption>
                    <div />
                    <TableHeaderPoints variant="small" colour="light-foreground-alt" weight="bold">{pointsHeader}</TableHeaderPoints>
                    {!isInfiniteTier && <TableHeaderMetric variant="small" colour="light-foreground-alt" weight="bold">{statusHeader}</TableHeaderMetric>}
                    {!isInfiniteTier && <TableHeaderMetric variant="small" colour="light-foreground-alt" weight="bold">{nightsHeader}</TableHeaderMetric>}
                  </TableHeader>
                </CSSBreakpoint>
                <ActivitiesList direction="vertical">
                  {activities.map(activity => <LoyaltyAccountLandingPageUserActivity
                    key={activity.id}
                    activity={activity}
                  />)}
                </ActivitiesList>
              </VerticalSpacer>)}
            </VerticalSpacer>
            {hasNextPage && <Group direction="horizontal" horizontalAlign="center">
              <TextButton kind="secondary" onClick={loadMore} disabled={isLoadingMore}>
                {showMoreLabel}
              </TextButton>
            </Group>}
          </VerticalSpacer>}
          <DisclaimerText variant="small" colour="light-foreground-alt" align="center">
            {disclaimerText}
          </DisclaimerText>
        </>}
      </VerticalSpacer>
    </LayoutContainer>
  </Sheet>
}

export default LoyaltyAccountLandingPageUserActivities
